import { commonConstants } from 'constants/index';
import { TrackingCustomEvent } from 'interfaces';

const { API_RESPONSE_TIME_BUCKETS } = commonConstants;

/**
 * Generates a suitable screenName based on route path,
 * following this format:
 * - / --> .
 * - /orders/marketplace --> .orders.marketplace
 * - /orders/sellers/:sellerId/:id --> .orders.sellers.&sellerId.&id
 *
 * @param path route path
 * @returns screenName to use in tracker-js
 */
const computeTrackingScreenName = (path: string) => {
  const PATTERN_REGEX = /[/:]/g;
  const replacedPattern: Record<string, string> = {
    '/': '.',
    ':': '&',
  };
  return path.replace(PATTERN_REGEX, value => replacedPattern[value]);
};

const getBucket = (responseTime: number) => {
  let low = 0;
  let high = API_RESPONSE_TIME_BUCKETS.length - 1;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);

    if (responseTime <= API_RESPONSE_TIME_BUCKETS[mid].value) {
      high = mid;
    } else {
      low = mid + 1;
    }
  }

  return API_RESPONSE_TIME_BUCKETS[low].label;
};

const errorStringify = (error: Error) => {
  return JSON.stringify({
    name: error.name,
    message: error.message,
    stack: error.stack,
  });
};

const nonBlockTrackError = (error: Error, contentName: string) => {
  const errorData = errorStringify(error);
  setTimeout(() => {
    track('customEvent', contentName, {
      action: 'error',
      category: 'staff-web-app',
      property: errorData,
    });
  }, 0);
};

const nonBlockTrackResponseTime = (
  contentName: string,
  duration: number,
  property?: Record<any, any>
) => {
  setTimeout(() => {
    track('customEvent', contentName, {
      action: 'apiCallTime',
      category: 'staff-web-app',
      label: getBucket(duration),
      value: duration,
      property: property ? JSON.stringify(property) : undefined,
    });
  }, 0);
};

const nonBlockTrackInvalidRespTime = (
  contentName: string,
  property?: Record<any, any>
) => {
  setTimeout(() => {
    track('customEvent', contentName, {
      action: 'invalid-apiCallTime',
      category: 'staff-web-app',
      property: property ? JSON.stringify(property) : undefined,
    });
  }, 0);
};

const nonBlockTrackCustomEvent = (
  contentName: string,
  event: TrackingCustomEvent
) => {
  setTimeout(() => {
    track('customEvent', contentName, event);
  }, 0);
};

const nonBlockTrackUserClickEvent = (
  regionName: string,
  contentName: string,
  payload?: string
) => {
  setTimeout(() => {
    track('manualTrackInteractionContent', {
      interaction: 'click',
      regionName,
      contentName,
      payload,
    });
  }, 0);
};

const nonBlockTrackResponseTimeWrapper = async <T>(
  apiCall: Promise<T>,
  contentName: string
): Promise<T> => {
  const startTime = Math.round(performance.now());
  let response = null;

  try {
    response = await apiCall;
  } catch (error) {
    throw error;
  }

  const endTime = Math.round(performance.now());
  const duration = endTime - startTime;

  nonBlockTrackResponseTime(contentName, duration);
  return response;
};

const nonBlockTrackRespTimeWithStartAt = (
  url?: any,
  startAt?: any,
  property?: Record<any, any>
) => {
  const contentName = String(url) || commonConstants.UNDEFINED_URL;
  const startAtNum = Number(startAt);

  if (!!startAt && !isNaN(startAtNum)) {
    const duration = Math.round(performance.now()) - startAtNum;
    nonBlockTrackResponseTime(contentName, duration, property);
  } else {
    nonBlockTrackInvalidRespTime(contentName, property);
  }
};

export default {
  computeTrackingScreenName,
  getBucket,
  nonBlockTrackResponseTime,
  nonBlockTrackResponseTimeWrapper,
  nonBlockTrackCustomEvent,
  nonBlockTrackUserClickEvent,
  nonBlockTrackError,
  nonBlockTrackRespTimeWithStartAt,
};
